import * as THREE from 'three';
import { modelLoader } from '../render/ktx2';
import { afterFirstView } from '../render/shaderWarmup';
import { tuneMeshyMaterial } from '../render/models/tune';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { qualityTier } from '../render/qualityTier';
import type { WeaponId } from './weaponData';

/**
 * Procedural weapon meshes in the house style (docs/WEAPONS.md, "Weapon models"). Each weapon is a
 * handful of primitives, side profiles and lathes, merged into one vertex-coloured geometry per finish
 * (metal, matte, wood), so a weapon is two or three draw calls. Geometry is built once per weapon and
 * level of detail and shared by every copy (the player's hand, other players, armed officers, street
 * pick-ups); materials are shared too.
 *
 * Two levels: `high` (close-ups, cut-scenes, the look-dev lineup) and `low` (the gameplay camera and
 * beyond, {@link WEAPON_LOD_DISTANCE}), which keeps every silhouette cue (the revolver's cylinder,
 * hammer spur and round butt, the sawn-off's twin barrels and pistol grip) and drops the small parts.
 *
 * Built in HAND space of the character rig: the grip sits at the hand (origin, palm at y ≈ -0.055)
 * and the business end points down the hand's local -Y. With the arm held out (aiming) that is
 * straight ahead; the top of a gun is the hand's local +Z. Each gun has a child named 'muzzle' at the
 * end of the barrel(s) for flashes and tracers.
 */

type Finish = 'metal' | 'matte' | 'wood';
type Detail = 'high' | 'low';
type Parts = Partial<Record<Finish, THREE.BufferGeometry>>;

/**
 * Camera distance (m) beyond which the low-detail model is drawn. The follow camera sits at 42 m and
 * more times the player's zoom setting (0.55–1.2, default 0.85: about 36 m on foot), so play draws the
 * low model unless the camera is zoomed right in; close-ups and cut-scenes draw the high one.
 */
export const WEAPON_LOD_DISTANCE = 24;

// Period palette. Blued steel is kept a touch lighter than black so a gun still reads against a dark coat.
const C = {
  blued: 0x353942,
  bluedHi: 0x4a4f59,
  caseHard: 0x6e6a62,
  steel: 0x8c9198,
  nickel: 0xc4c8cc,
  bore: 0x0a0a0b,
  walnut: 0x6a3f22,
  walnutDark: 0x4a2a16,
  rawWood: 0xb88a58,
  willow: 0xe0c78f,
  cane: 0xa47a46,
  tape: 0x2d2b29,
  tapeHi: 0x3a3632,
  leather: 0x5a2a1a,
  leatherDark: 0x2e150d,
  thread: 0x9b7b55,
  paint: 0x6c2c22,
  rust: 0x7a3d20,
  horn: 0x5a4030,
  ballMark: 0xa23c2c,
  card: 0x9c7a4c,
  label: 0xd9cfae,
};

let shared: { mats: Record<string, THREE.MeshStandardMaterial>; grain: THREE.DataTexture } | null = null;
let users = 0;
const geoCache = new Map<string, Parts>();
let ammoGeo: Parts | null = null;

/** Straight-grained wood: dark late-wood lines along V over a pale ground, a little waviness. Tiles. */
function grainTexture(): THREE.DataTexture {
  const n = 64;
  const data = new Uint8Array(n * n * 4);
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const u = i / n;
      const v = j / n;
      const g = u * 7 + 0.08 * Math.sin(v * Math.PI * 2 * 2 + u * 9) + 0.04 * Math.sin(v * Math.PI * 2 * 5);
      const f = g - Math.floor(g);
      const line = Math.exp(-(((f - 0.5) / 0.07) ** 2));
      const fine = Math.exp(-(((((g * 3) % 1) - 0.5) / 0.12) ** 2));
      const noise = (Math.sin(i * 12.9898 + j * 78.233) * 43758.5453) % 1;
      const lum = 0.97 - 0.3 * line - 0.08 * fine + 0.03 * noise;
      const b = Math.max(0, Math.min(255, Math.round(lum * 255)));
      data.set([b, b, b, 255], (j * n + i) * 4);
    }
  }
  const t = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.colorSpace = THREE.SRGBColorSpace;
  t.needsUpdate = true;
  return t;
}

function materials(): Record<string, THREE.MeshStandardMaterial> {
  if (!shared) {
    const grain = grainTexture();
    shared = {
      grain,
      mats: {
        // The finishes the models are drawn with (vertex colours carry the tone).
        metal: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.34, metalness: 0.72 }),
        matte: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8 }),
        wood: new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.55, map: grain }),
        // Spent cases and cartridges (instanced by the view).
        brass: new THREE.MeshStandardMaterial({ color: 0xc9a24a, roughness: 0.35, metalness: 0.85 }),
        shell: new THREE.MeshStandardMaterial({ color: 0x9c2a1c, roughness: 0.55 }),
      },
    };
  }
  return shared.mats;
}

/** Take / release the shared materials (the view system does this once). */
export function acquireWeaponMaterials(): Record<string, THREE.MeshStandardMaterial> {
  users++;
  loadWeaponModels();
  return materials();
}

export function releaseWeaponMaterials(): void {
  if (--users > 0 || !shared) return;
  for (const m of Object.values(shared.mats)) m.dispose();
  shared.grain.dispose();
  shared = null;
  for (const parts of geoCache.values()) for (const g of Object.values(parts)) g.dispose();
  geoCache.clear();
  if (ammoGeo) for (const g of Object.values(ammoGeo)) g.dispose();
  ammoGeo = null;
}

// ------------------------------------------------------------------------------------------------
// Geometry kit: every part is flattened to non-indexed position / normal / uv / colour and merged.

interface PartOpts {
  /** Wood: grain pitch (lines per metre / 7); the UVs are projected from hand space at this scale. */
  grain?: number;
  /** Wood: the hand axis the grain runs along (default Y, the length of most things held). */
  along?: 'y' | 'z';
  /** Faceted (hex bar): flat normals. */
  flat?: boolean;
}

/**
 * Wood UVs projected from hand space, triangle by triangle, so the grain runs along the piece on every
 * face (caps, extruded edges, round handles alike): V follows the grain axis, U the other axis lying in
 * the face. The texture's late-wood lines run along V.
 */
function projectGrain(g: THREE.BufferGeometry, along: 'y' | 'z', scale: number): void {
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  const uv = g.getAttribute('uv') as THREE.BufferAttribute;
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const nrm = new THREE.Vector3();
  const e = new THREE.Vector3();
  const vi = along === 'y' ? 1 : 2;
  const others = along === 'y' ? [0, 2] : [0, 1];
  for (let t = 0; t + 2 < pos.count; t += 3) {
    a.fromBufferAttribute(pos, t);
    b.fromBufferAttribute(pos, t + 1);
    c.fromBufferAttribute(pos, t + 2);
    nrm.subVectors(c, b).cross(e.subVectors(a, b));
    // U along whichever of the two cross axes lies most in the face (the one the normal ignores).
    const ui = Math.abs(nrm.getComponent(others[0]!)) <= Math.abs(nrm.getComponent(others[1]!)) ? others[0]! : others[1]!;
    for (let k = 0; k < 3; k++) {
      const p = k === 0 ? a : k === 1 ? b : c;
      uv.setXY(t + k, p.getComponent(ui) * scale, p.getComponent(vi) * scale);
    }
  }
}

class Kit {
  private parts: Record<Finish, THREE.BufferGeometry[]> = { metal: [], matte: [], wood: [] };

  add(finish: Finish, geo: THREE.BufferGeometry, color: number, o: PartOpts = {}): void {
    const g = geo.index ? geo.toNonIndexed() : geo;
    if (g !== geo) geo.dispose();
    g.clearGroups();
    for (const name of Object.keys(g.attributes)) if (name !== 'position' && name !== 'normal' && name !== 'uv') g.deleteAttribute(name);
    const n = g.getAttribute('position').count;
    if (o.flat || !g.getAttribute('normal')) g.computeVertexNormals();
    if (!g.getAttribute('uv')) g.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(n * 2), 2));
    if (finish === 'wood') projectGrain(g, o.along ?? 'y', o.grain ?? 16);
    const c = new THREE.Color(color);
    const col = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) col.set([c.r, c.g, c.b], i * 3);
    g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    this.parts[finish].push(g);
  }

  build(): Parts {
    const out: Parts = {};
    for (const f of ['metal', 'matte', 'wood'] as const) {
      const list = this.parts[f];
      if (!list.length) continue;
      const merged = list.length === 1 ? list[0]! : mergeGeometries(list)!;
      if (merged !== list[0]) for (const g of list) g.dispose();
      merged.computeBoundingSphere();
      merged.userData.weaponShared = true;
      out[f] = merged;
    }
    return out;
  }
}

/** A box: `w` across (X), `l` along the barrel (Y), `h` up (Z), centred at (x, y, z). */
function box(w: number, l: number, h: number, x: number, y: number, z: number): THREE.BufferGeometry {
  return new THREE.BoxGeometry(w, l, h).translate(x, y, z);
}

/** A cylinder along the hand's Y from y0 down to y1 (y1 < y0), radius r0 at y0 and r1 at y1. */
function rodY(r0: number, r1: number, y0: number, y1: number, seg: number, x = 0, z = 0): THREE.BufferGeometry {
  return new THREE.CylinderGeometry(r0, r1, y0 - y1, seg, 1).translate(x, (y0 + y1) / 2, z);
}

/** A cylinder across the hand (along X), length `len`, centred at (x, y, z). */
function rodX(r: number, len: number, seg: number, x: number, y: number, z: number): THREE.BufferGeometry {
  return new THREE.CylinderGeometry(r, r, len, seg, 1).rotateZ(Math.PI / 2).translate(x, y, z);
}

/** A cylinder standing up the hand's Z, height `h`, centred at (x, y, z). */
function rodZ(r: number, h: number, seg: number, x: number, y: number, z: number): THREE.BufferGeometry {
  return new THREE.CylinderGeometry(r, r, h, seg, 1).rotateX(Math.PI / 2).translate(x, y, z);
}

/** Shape (u, v) → hand (X = depth, Y = u, Z = v): a side profile drawn in the barrel's plane. */
const SIDE = new THREE.Matrix4().set(0, 0, 1, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1);

/** Extrude a side profile (shape coordinates = hand Y, hand Z) `thick` across X, centred on x. */
function side(shape: THREE.Shape, thick: number, curveSegments = 4, x = 0): THREE.BufferGeometry {
  return new THREE.ExtrudeGeometry(shape, { depth: thick, bevelEnabled: false, curveSegments })
    .translate(0, 0, -thick / 2)
    .applyMatrix4(SIDE)
    .translate(x, 0, 0);
}

/** Extrude a face profile (shape coordinates = hand X, hand Y) `thick` along Z, centred on z. */
function face(shape: THREE.Shape, thick: number, curveSegments = 4, z = 0): THREE.BufferGeometry {
  return new THREE.ExtrudeGeometry(shape, { depth: thick, bevelEnabled: false, curveSegments }).translate(0, 0, z - thick / 2);
}

function poly(pts: [number, number][]): THREE.Shape {
  return new THREE.Shape(pts.map(([a, b]) => new THREE.Vector2(a, b)));
}

/** A ring segment (trigger guards) in the side plane, from angle a0 to a1 (radians, shape space). */
function arcShape(cu: number, cv: number, rOut: number, rIn: number, a0: number, a1: number): THREE.Shape {
  const s = new THREE.Shape();
  s.absarc(cu, cv, rOut, a0, a1, false);
  s.absarc(cu, cv, rIn, a1, a0, true);
  return s;
}

/**
 * A revolver cylinder along Y with `flutes` V-grooves on its waist: the side rings are pulled in to the
 * flute ends and every third column sunk, so the grooves run out before either face as on the real thing.
 */
function flutedCylinder(r: number, y0: number, y1: number, flutes: number, depth: number): THREE.BufferGeometry {
  const seg = flutes * 3;
  const geo = new THREE.CylinderGeometry(r, r, y0 - y1, seg, 3);
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  const half = (y0 - y1) / 2;
  for (let row = 1; row <= 2; row++) {
    for (let col = 0; col <= seg; col++) {
      const i = row * (seg + 1) + col;
      pos.setY(i, row === 1 ? half * 0.6 : -half * 0.6);
      if (col % 3 === 1) pos.setXYZ(i, pos.getX(i) * (1 - depth), pos.getY(i), pos.getZ(i) * (1 - depth));
    }
  }
  // Offset the flutes half a step so one sits under the top strap, as when the gun is closed.
  geo.rotateY(Math.PI / seg);
  geo.computeVertexNormals();
  return geo.translate(0, (y0 + y1) / 2, 0);
}

// ------------------------------------------------------------------------------------------------
// The models. Coordinates are hand space before WEAPON_VISUAL_SCALE.

/** Service-pattern .38: swing-out fluted cylinder, top strap, spur hammer, round-butt walnut grip, lanyard ring. */
function revolver(k: Kit, d: Detail): void {
  const hi = d === 'high';
  const BZ = 0.03; // bore axis height
  // Frame: one side profile — recoil shield, top strap over the cylinder, front lug, bottom under it.
  const frame = poly([
    [-0.05, 0.002],
    [-0.042, 0.012],
    [-0.04, 0.042],
    [-0.046, 0.052],
    [-0.054, 0.057],
    [-0.113, 0.057],
    [-0.118, 0.053],
    [-0.12, 0.016],
    [-0.113, 0.006],
    [-0.074, 0.006],
    [-0.066, 0.0],
    [-0.056, 0.0],
  ]);
  k.add('metal', side(frame, 0.016), C.blued);
  // Cylinder: fluted, with the chamber mouths showing on the front face.
  const cy0 = -0.062;
  const cy1 = -0.108;
  k.add('metal', (hi ? flutedCylinder(0.0215, cy0, cy1, 6, 0.2) : rodY(0.0215, 0.0215, cy0, cy1, 8)).translate(0, 0, BZ), C.blued);
  // Barrel, ejector-rod shroud underneath, rib and ramp front sight on top.
  k.add('metal', rodY(0.0088, 0.0088, -0.118, -0.207, hi ? 12 : 6, 0, BZ), C.blued);
  if (hi) {
    k.add('metal', rodY(0.0048, 0.0048, -0.118, -0.163, 8, 0, BZ - 0.0122), C.blued);
    k.add('metal', rodY(0.0052, 0.0034, -0.163, -0.167, 8, 0, BZ - 0.0122), C.bluedHi);
    k.add('metal', box(0.0045, 0.088, 0.004, 0, -0.162, BZ + 0.0095), C.blued);
    k.add('metal', side(poly([[-0.186, BZ + 0.009], [-0.205, BZ + 0.009], [-0.204, BZ + 0.02], [-0.197, BZ + 0.02]]), 0.0035), C.blued);
    k.add('matte', rodY(0.0046, 0.0046, -0.2068, -0.2078, 8, 0, BZ), C.bore);
    // Rear sight groove: a dark line down the top strap.
    k.add('matte', box(0.0022, 0.05, 0.0012, 0, -0.084, 0.0574), C.bore);
    // Recoil shield ring behind the cylinder, latch on the left of the frame, a side-plate screw on the right.
    k.add('metal', rodY(0.0185, 0.0185, -0.057, -0.062, 12, 0, BZ), C.bluedHi);
    k.add('metal', box(0.004, 0.012, 0.007, -0.0095, -0.053, 0.034), C.bluedHi);
    k.add('metal', rodX(0.0026, 0.0172, 6, 0, -0.0465, 0.022), C.steel);
    // Chamber mouths.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
      k.add('matte', rodY(0.0045, 0.0045, cy1 + 0.0006, cy1 - 0.0006, 6, Math.cos(a) * 0.013, BZ + Math.sin(a) * 0.013), C.bore);
    }
  } else {
    k.add('metal', box(0.004, 0.01, 0.012, 0, -0.2, BZ + 0.012), C.blued);
  }
  // Spur hammer: cocked back over the grip, the part everyone recognises in silhouette.
  k.add(
    'metal',
    side(
      poly([
        [-0.05, 0.036],
        [-0.051, 0.052],
        [-0.046, 0.06],
        [-0.036, 0.0665],
        [-0.03, 0.066],
        [-0.031, 0.061],
        [-0.039, 0.055],
        [-0.042, 0.036],
      ]),
      0.0075,
    ),
    C.bluedHi,
  );
  // Trigger guard and the trigger inside it.
  k.add('metal', side(arcShape(-0.0705, 0.004, 0.0175, 0.0142, Math.PI, Math.PI * 2), 0.006, hi ? 8 : 4), C.blued);
  k.add(
    'metal',
    side(
      poly([
        [-0.066, 0.004],
        [-0.073, 0.004],
        [-0.072, -0.004],
        [-0.068, -0.01],
        [-0.064, -0.0112],
        [-0.066, -0.004],
      ]),
      0.005,
    ),
    C.steel,
  );
  // Grip: steel grip frame (shows as the front and back straps) under two curved walnut panels.
  const grip = (s: number): THREE.Shape => {
    const cx = -0.04;
    const cz = -0.03;
    const p = (u: number, v: number): [number, number] => [cx + (u - cx) * s, cz + (v - cz) * s];
    const sh = new THREE.Shape();
    sh.moveTo(...p(-0.055, 0.004));
    sh.quadraticCurveTo(...p(-0.06, -0.03), ...p(-0.052, -0.062));
    sh.quadraticCurveTo(...p(-0.047, -0.077), ...p(-0.034, -0.077));
    sh.quadraticCurveTo(...p(-0.021, -0.075), ...p(-0.021, -0.061));
    sh.quadraticCurveTo(...p(-0.021, -0.03), ...p(-0.03, -0.003));
    sh.quadraticCurveTo(...p(-0.034, 0.014), ...p(-0.044, 0.016));
    sh.lineTo(...p(-0.049, 0.008));
    sh.closePath();
    return sh;
  };
  if (hi) k.add('metal', side(grip(1), 0.0115, 5), C.blued);
  k.add('wood', side(grip(hi ? 0.9 : 1), hi ? 0.022 : 0.02, hi ? 5 : 2), C.walnut, { grain: 24, along: 'z' });
  if (hi) {
    // Panel screw through both panels, lanyard ring at the butt.
    k.add('metal', rodX(0.0026, 0.0232, 8, 0, -0.039, -0.032), C.steel);
    k.add('metal', new THREE.TorusGeometry(0.0062, 0.0013, 4, 10).rotateY(Math.PI / 2).translate(0, -0.032, -0.0835), C.steel);
    k.add('metal', box(0.004, 0.006, 0.004, 0, -0.033, -0.0775), C.steel);
  }
}

/** Side-by-side 12-bore, barrels sawn to a hand's length, boxlock action on a visible hinge, stock cut to a pistol grip. */
function sawnoff(k: Kit, d: Detail): void {
  const hi = d === 'high';
  const BZ = 0.03;
  const XB = 0.0135;
  const y0 = -0.05; // breech
  const y1 = -0.405; // the hacksaw cut
  for (const x of [-XB, XB]) {
    k.add('metal', rodY(0.013, 0.013, y0, y1, hi ? 12 : 7, x, BZ), C.blued);
    if (hi) {
      k.add('matte', rodY(0.0098, 0.0098, y1 + 0.0004, y1 - 0.0008, 10, x, BZ), C.bore);
      // A bright ring where the saw went through the blueing.
      k.add('metal', rodY(0.0131, 0.0131, y1 + 0.0035, y1, 12, x, BZ), C.steel);
    }
  }
  // Ribs in the valleys between the tubes.
  k.add('metal', box(0.008, y0 - y1, 0.004, 0, (y0 + y1) / 2, BZ + 0.0112), C.bluedHi);
  if (hi) k.add('metal', box(0.006, 0.2, 0.004, 0, -0.16, BZ - 0.0112), C.blued);
  // Splinter fore-end under the barrels, with its iron.
  k.add(
    'wood',
    side(
      poly([
        [-0.08, BZ - 0.01],
        [-0.215, BZ - 0.011],
        [-0.224, BZ - 0.019],
        [-0.215, BZ - 0.031],
        [-0.08, BZ - 0.031],
      ]),
      0.032,
    ),
    C.walnut,
    { grain: 16 },
  );
  if (hi) k.add('metal', box(0.012, 0.014, 0.003, 0, -0.2, BZ - 0.0315), C.caseHard);
  // Action: standing breech and the bar forward under the barrels to the hinge knuckle.
  k.add(
    'metal',
    side(
      poly([
        [y0, BZ + 0.012],
        [-0.016, BZ + 0.012],
        [-0.008, BZ + 0.004],
        [-0.008, -0.004],
        [-0.088, -0.004],
        [-0.098, 0.004],
        [-0.096, BZ - 0.013],
        [y0, BZ - 0.013],
      ]),
      0.04,
    ),
    C.caseHard,
  );
  // Hinge: the knuckle and its cross pin, proud on both sides.
  k.add('metal', rodX(0.0095, 0.042, hi ? 12 : 6, 0, -0.092, 0.006), C.caseHard);
  if (hi) {
    k.add('metal', rodX(0.0045, 0.0446, 8, 0, -0.092, 0.006), C.steel);
    // Break line at the breech face, the top lever and the safety on the tang.
    k.add('matte', box(0.054, 0.0014, 0.028, 0, y0 - 0.0002, BZ), C.bore);
    k.add('metal', box(0.004, 0.012, 0.0025, 0, -0.004, BZ + 0.0128), C.steel);
  }
  // Top lever, swung a little off the centre line.
  k.add('metal', new THREE.BoxGeometry(0.008, 0.024, 0.0045).rotateZ(0.35).translate(0.005, -0.022, BZ + 0.0142), C.steel);
  // Trigger guard and the two triggers.
  k.add('metal', side(arcShape(-0.008, -0.002, 0.018, 0.0148, Math.PI, Math.PI * 2), 0.006, hi ? 8 : 4), C.caseHard);
  if (hi) {
    for (const y of [-0.0135, -0.0035]) k.add('metal', side(poly([[y + 0.002, -0.002], [y - 0.002, -0.002], [y - 0.0015, -0.013], [y + 0.0025, -0.014]]), 0.004), C.steel);
  }
  // Stock: the wrist and a pistol grip, the butt sawn off square behind the hand.
  const stock = new THREE.Shape();
  stock.moveTo(-0.01, BZ + 0.011);
  stock.lineTo(0.04, BZ + 0.002);
  stock.lineTo(0.074, BZ - 0.004);
  stock.lineTo(0.082, BZ - 0.05);
  stock.lineTo(0.062, BZ - 0.064);
  stock.quadraticCurveTo(0.03, BZ - 0.052, 0.013, BZ - 0.027);
  stock.lineTo(-0.01, BZ - 0.027);
  stock.closePath();
  k.add('wood', side(stock, 0.032, hi ? 5 : 2), C.walnut, { grain: 16 });
  // The sawn face: raw, pale wood.
  k.add(
    'matte',
    side(
      poly([
        [0.074, BZ - 0.004],
        [0.0765, BZ - 0.0045],
        [0.0845, BZ - 0.0505],
        [0.082, BZ - 0.05],
      ]),
      0.0334,
    ),
    C.rawWood,
  );
}

/** Leather cosh: a stitched sock with a lead-weighted head, tied off at the neck, and a wrist strap. */
function cosh(k: Kit, d: Detail): void {
  const hi = d === 'high';
  const prof: [number, number][] = [
    [0, 0.016],
    [0.011, 0.016],
    [0.015, 0.012],
    [0.0155, 0],
    [0.0145, -0.04],
    [0.0135, -0.086],
    [0.016, -0.1],
    [0.024, -0.118],
    [0.031, -0.14],
    [0.0345, -0.16],
    [0.0315, -0.182],
    [0.022, -0.199],
    [0.01, -0.207],
    [0, -0.209],
  ];
  k.add('matte', new THREE.LatheGeometry(prof.map(([r, y]) => new THREE.Vector2(r, y)), hi ? 12 : 6), C.leather);
  // Wrist strap: a flat loop round the wrist, its tail into the butt.
  k.add('matte', new THREE.TorusGeometry(0.034, 0.003, 3, hi ? 14 : 8).rotateX(Math.PI / 2).scale(1, 2.2, 1).translate(0, 0.03, 0), C.leatherDark);
  k.add('matte', box(0.006, 0.02, 0.004, 0, 0.024, -0.03), C.leatherDark);
  if (hi) {
    // Tie at the neck, stitched seam down the side.
    k.add('matte', new THREE.TorusGeometry(0.0158, 0.0025, 4, 12).rotateX(Math.PI / 2).translate(0, -0.093, 0), C.leatherDark);
    k.add('matte', box(0.0018, 0.1, 0.003, 0.0148, -0.042, 0), C.thread);
    k.add('matte', box(0.0018, 0.07, 0.003, 0.0328, -0.155, 0), C.thread);
    k.add('matte', new THREE.TorusGeometry(0.0118, 0.0022, 4, 10).rotateX(Math.PI / 2).translate(0, 0.013, 0), C.leatherDark);
  }
}

/** Cricket bat: taped cane handle, splice, willow blade with shoulders, a spine on the back and old ball marks. */
function bat(k: Kit, d: Detail): void {
  const hi = d === 'high';
  if (hi) {
    // Tape wound in overlapping turns (each band tipped a little so the seams read as a spiral).
    const turns = 7;
    for (let i = 0; i < turns; i++) {
      const yA = 0.058 - i * 0.034;
      k.add('matte', rodY(0.0172, 0.0172, yA, yA - 0.036, 10).translate(0, -yA + 0.018, 0).rotateX(0.045).translate(0, yA - 0.018, 0), i % 2 ? C.tape : C.tapeHi);
    }
    k.add('matte', rodY(0.0135, 0.0172, 0.074, 0.06, 10), C.tape);
    k.add('wood', rodY(0.0158, 0.0166, -0.18, -0.25, 10), C.cane, { grain: 24 });
  } else {
    k.add('matte', rodY(0.0172, 0.0172, 0.074, -0.18, 6), C.tape);
    k.add('wood', rodY(0.0158, 0.0166, -0.18, -0.25, 6), C.cane);
  }
  // Blade: sloping shoulders, square edges, a rounded toe.
  const blade = new THREE.Shape();
  blade.moveTo(-0.054, -0.27);
  blade.quadraticCurveTo(-0.051, -0.244, -0.018, -0.24);
  blade.lineTo(0.018, -0.24);
  blade.quadraticCurveTo(0.051, -0.244, 0.054, -0.27);
  blade.lineTo(0.054, -0.735);
  blade.quadraticCurveTo(0.053, -0.751, 0.04, -0.753);
  blade.lineTo(-0.04, -0.753);
  blade.quadraticCurveTo(-0.053, -0.751, -0.054, -0.735);
  blade.closePath();
  k.add('wood', face(blade, 0.026, hi ? 4 : 1), C.willow, { grain: 8 });
  // Spine: the hump down the back (+Z), thickest low in the blade where the middle is.
  const spine: [number, number][] = [
    [0, -0.27],
    [0.55, -0.33],
    [0.85, -0.45],
    [1, -0.6],
    [0.8, -0.71],
    [0, -0.745],
  ];
  k.add('wood', new THREE.LatheGeometry(spine.map(([r, y]) => new THREE.Vector2(r, y)), hi ? 10 : 6).scale(0.032, 1, 0.018).translate(0, 0, 0.006), C.willow, { grain: 8 });
  if (hi) {
    // The splice: the cane handle's V let into the shoulders, both faces.
    k.add('wood', face(poly([[-0.017, -0.2405], [0.017, -0.2405], [0.002, -0.33], [-0.002, -0.33]]), 0.0266), C.cane, { grain: 30 });
    // Red cherry marks from the ball on the face (-Z).
    for (const [x, y, r] of [[0.012, -0.58, 0.009], [-0.02, -0.63, 0.007], [0.004, -0.66, 0.006]] as const) {
      k.add('matte', new THREE.CircleGeometry(r, 8).rotateY(Math.PI).scale(1, 1.3, 1).translate(x, y, -0.0132), C.ballMark);
    }
  }
}

/** Crowbar: hexagonal bar painted oxide red, chisel end behind the fist, gooseneck with a split claw. */
function crowbar(k: Kit, d: Detail): void {
  const hi = d === 'high';
  const R = 0.0115;
  k.add('matte', rodY(R, R, 0.1, -0.47, 6).rotateY(Math.PI / 6), C.paint, { flat: true });
  // Chisel end, flattened and kinked, worn bright.
  k.add(
    'metal',
    side(poly([[0.098, -R], [0.098, R], [0.152, 0.004], [0.156, 0.001], [0.152, -0.002]]), 0.02)
      .translate(0, -0.1, 0)
      .rotateX(-0.22)
      .translate(0, 0.1, 0),
    C.steel,
    { flat: true },
  );
  // Gooseneck: the hex bar bent up and back over itself.
  const neck = new THREE.CubicBezierCurve3(new THREE.Vector3(0, -0.468, 0), new THREE.Vector3(0, -0.56, 0), new THREE.Vector3(0, -0.605, 0.075), new THREE.Vector3(0, -0.556, 0.104));
  k.add('matte', new THREE.TubeGeometry(neck, hi ? 10 : 4, R, 6, false), C.paint, { flat: true });
  // Split claw at the end of the hook, bare steel.
  const t = neck.getTangent(1);
  const tip = neck.getPoint(1);
  const ang = Math.atan2(t.z, t.y);
  for (const s of hi ? [-1, 1] : [0]) {
    const w = hi ? 0.0085 : 0.02;
    k.add('metal', box(w, 0.032, 0.006, s * 0.0062, 0.012, 0).rotateZ(s * 0.12).rotateX(ang).translate(tip.x, tip.y, tip.z), C.steel, { flat: true });
  }
  if (hi) {
    // Paint worn through where it's been used, a lick of rust at the bend.
    k.add('metal', rodY(R * 1.01, R * 1.01, 0.1, 0.07, 6).rotateY(Math.PI / 6), C.steel, { flat: true });
    k.add('matte', rodY(R * 1.03, R * 1.03, -0.4, -0.428, 6).rotateY(Math.PI / 6), C.rust, { flat: true });
    k.add('matte', rodY(R * 1.03, R * 1.03, -0.2, -0.212, 6).rotateY(Math.PI / 6), C.rust, { flat: true });
  }
}

/** Flick knife (stiletto pattern): horn scales between nickel bolsters, a small guard, push button, spear blade out. */
function knife(k: Kit, d: Detail): void {
  const hi = d === 'high';
  // Scales.
  const handle = new THREE.Shape();
  handle.moveTo(-0.0095, 0.013);
  handle.lineTo(0.0095, 0.013);
  handle.quadraticCurveTo(0.0108, -0.03, 0.0095, -0.069);
  handle.lineTo(-0.0095, -0.069);
  handle.quadraticCurveTo(-0.0108, -0.03, -0.0095, 0.013);
  k.add('wood', face(handle, 0.0115, hi ? 4 : 1), C.horn, { grain: 40 });
  // Bolsters: rounded pommel and a square front with the guard.
  const pommel = new THREE.Shape();
  pommel.moveTo(-0.0102, 0.012);
  pommel.lineTo(0.0102, 0.012);
  pommel.lineTo(0.0102, 0.018);
  pommel.quadraticCurveTo(0.0102, 0.027, 0, 0.027);
  pommel.quadraticCurveTo(-0.0102, 0.027, -0.0102, 0.018);
  pommel.closePath();
  k.add('metal', face(pommel, 0.0128, hi ? 4 : 1), C.nickel);
  k.add('metal', box(0.0205, 0.012, 0.0128, 0, -0.075, 0), C.nickel);
  k.add('metal', rodX(0.0028, 0.034, hi ? 8 : 4, 0, -0.0815, 0), C.nickel);
  // Blade: spear point, with a raised centre grind on the high model.
  const blade = poly([
    [-0.0062, -0.082],
    [0.0062, -0.082],
    [0.0074, -0.096],
    [0.0068, -0.15],
    [0.0045, -0.18],
    [0, -0.207],
    [-0.0045, -0.18],
    [-0.0068, -0.15],
    [-0.0074, -0.096],
  ]);
  k.add('metal', face(blade, 0.0026), C.nickel);
  if (hi) {
    k.add('metal', face(poly([[-0.0018, -0.096], [0.0018, -0.096], [0.0012, -0.17], [0, -0.198], [-0.0012, -0.17]]), 0.0034), C.steel);
    k.add('metal', rodZ(0.0034, 0.0022, 8, 0, -0.058, 0.0062), C.nickel);
  }
}

const BUILDERS: Partial<Record<WeaponId, (k: Kit, d: Detail) => void>> = { revolver, sawnoff, cosh, bat, crowbar, knife };

/** Shared geometry (one per finish) for a weapon at a level of detail. Built once, kept until the materials go. */
export function weaponGeometry(id: WeaponId, detail: Detail): Parts {
  const key = `${id}:${detail}`;
  let parts = geoCache.get(key);
  if (!parts) {
    const k = new Kit();
    BUILDERS[id]?.(k, detail);
    parts = k.build();
    geoCache.set(key, parts);
  }
  return parts;
}

/** Triangles in a weapon's model (look-dev and budget tests). */
export function weaponTriangles(id: WeaponId, detail: Detail): number {
  let n = 0;
  for (const g of Object.values(weaponGeometry(id, detail))) n += g.getAttribute('position').count / 3;
  return n;
}

/**
 * Drawn a little larger than life (like the characters themselves) so a revolver still reads from
 * the high gameplay camera.
 */
export const WEAPON_VISUAL_SCALE: Readonly<Record<WeaponId, number>> = { fists: 1, cosh: 1.3, bat: 1.15, crowbar: 1.2, knife: 1.5, revolver: 1.55, sawnoff: 1.35 };

/** Muzzle positions (hand space): where flashes and tracers start. */
const MUZZLE: Partial<Record<WeaponId, [number, number]>> = { revolver: [-0.208, 0.03], sawnoff: [-0.406, 0.03] };

function level(id: WeaponId, detail: Detail): THREE.Group {
  const m = materials();
  const g = new THREE.Group();
  g.name = detail;
  for (const [finish, geo] of Object.entries(weaponGeometry(id, detail)) as [Finish, THREE.BufferGeometry][]) {
    const mesh = new THREE.Mesh(geo, m[finish]!);
    mesh.castShadow = true;
    g.add(mesh);
  }
  return g;
}

// ------------------------------------------------------------------------------------------------
// Meshy models (docs/WEAPONS.md, "Meshy models"): one textured mesh per weapon in public/models, in
// Meshy's own units and axes. Each is turned, scaled and placed into hand space by the table below and
// replaces the procedural `high` model; the procedural `low` model stays as the far level, and the LOW
// quality tier keeps the procedural pair. Loaded once, on first use of the weapon materials; a weapon
// built before the files arrive draws the procedural model and is upgraded in place when they do.

/** Draw the Meshy model out to here (m), the procedural low model beyond: covers the follow camera at any zoom. */
export const WEAPON_GLB_DISTANCE = 60;

interface GlbFit {
  /** Hand-space images of the file's +X, +Y, +Z axes (a rotation). */
  axes: [THREE.Vector3Tuple, THREE.Vector3Tuple, THREE.Vector3Tuple];
  /** Real length (m, hand space before WEAPON_VISUAL_SCALE) along the hand's Y. */
  length: number;
  /** Hand Y of the rear end (butt, pommel, handle end): where the grip falls in the fist. */
  rear: number;
  /** Hand Z of the top of the model (guns: puts the bore line at 0.03) — or, with `mid`, of its middle. */
  top?: number;
  mid?: number;
  /** Guns: the bore's depth under the top (m), for the muzzle anchor. */
  bore?: number;
  /** Lower roughness so steel reads (the build sets 0.9 everywhere). */
  roughness?: number;
}

// Axes worked out from the files' bounds (the long axis, which end is wide, where the grip hangs).
const GUN: GlbFit['axes'] = [
  [0, 1, 0],
  [0, 0, 1],
  [1, 0, 0],
]; // muzzle at file -X, top at file +Y
const GLB_FIT: Partial<Record<WeaponId, GlbFit>> = {
  revolver: { axes: GUN, length: 0.26, rear: -0.012, top: 0.044, bore: 0.014, roughness: 0.6 },
  sawnoff: { axes: GUN, length: 0.55, rear: 0.085, top: 0.066, bore: 0.036, roughness: 0.65 },
  bat: { axes: [[1, 0, 0], [0, 1, 0], [0, 0, 1]], length: 0.86, rear: 0.074, mid: 0 }, // handle at file +Y
  crowbar: { axes: GUN, length: 0.75, rear: 0.156, mid: -0.014 }, // claw at file -X, chisel at +X
  cosh: { axes: [[0, -1, 0], [0, 0, 1], [-1, 0, 0]], length: 0.25, rear: 0.06, mid: 0 }, // head at file +X
  knife: { axes: [[0, 1, 0], [1, 0, 0], [0, 0, -1]], length: 0.22, rear: 0.03, mid: 0, roughness: 0.5 }, // blade at file -X
};

interface GlbModel {
  geo: THREE.BufferGeometry;
  mat: THREE.MeshStandardMaterial;
  muzzle: THREE.Vector3 | null;
}

const BASE = import.meta.env?.BASE_URL ?? './';
const glbModels = new Map<WeaponId, GlbModel>();
let glbLoading = false;
/** Weapons built (procedural) while the files load: upgraded when they arrive. */
const pendingUpgrade = new Map<THREE.Group, WeaponId>();

/** Bake the file's mesh into hand space by its fit: one shared geometry and material per weapon. */
function fitGlb(scene: THREE.Object3D, fit: GlbFit): GlbModel | null {
  let src: THREE.Mesh | null = null;
  scene.updateMatrixWorld(true);
  scene.traverse((o) => {
    if (!src && (o as THREE.Mesh).isMesh) src = o as THREE.Mesh;
  });
  if (!src) return null;
  const mesh: THREE.Mesh = src;
  const geo = mesh.geometry.clone().applyMatrix4(mesh.matrixWorld);
  const [ax, ay, az] = fit.axes.map((a) => new THREE.Vector3(...a));
  geo.applyMatrix4(new THREE.Matrix4().makeBasis(ax!, ay!, az!));
  geo.computeBoundingBox();
  let b = geo.boundingBox!;
  const s = fit.length / (b.max.y - b.min.y);
  geo.scale(s, s, s);
  geo.computeBoundingBox();
  b = geo.boundingBox!;
  const z = fit.top !== undefined ? fit.top - b.max.z : (fit.mid ?? 0) - (b.min.z + b.max.z) / 2;
  geo.translate(-(b.min.x + b.max.x) / 2, fit.rear - b.max.y, z);
  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  geo.userData.weaponShared = true;
  b = geo.boundingBox!;
  // the shared Meshy material policy (render/models/tune.ts); the weapons keep the file's environment level
  const src0 = mesh.material as THREE.MeshStandardMaterial;
  const mat = tuneMeshyMaterial(src0.clone(), { roughness: fit.roughness ?? 0.9, envMapIntensity: src0.envMapIntensity });
  return { geo, mat, muzzle: fit.bore !== undefined ? new THREE.Vector3(0, b.min.y, b.max.z - fit.bore) : null };
}

/** Start loading the Meshy weapon models (once; browser only; a failed file keeps its procedural model). */
export function loadWeaponModels(): void {
  if (glbLoading || typeof document === 'undefined') return;
  glbLoading = true;
  const loader = modelLoader();
  const ids = Object.keys(GLB_FIT) as WeaponId[];
  void Promise.all(
    ids.map((id) =>
      // after the first view's textures (render/shaderWarmup.ts afterFirstView)
      afterFirstView()
        .then(() => loader.loadAsync(`${BASE}models/weapon-${id}.glb`))
        .then((gltf) => {
          const m = fitGlb(gltf.scene, GLB_FIT[id]!);
          if (m) glbModels.set(id, m);
        })
        .catch(() => undefined),
    ),
  ).then(() => {
    for (const [g, id] of pendingUpgrade) if (glbModels.has(id)) fillWeapon(g, id);
    pendingUpgrade.clear();
  });
}

/** True when a weapon draws its Meshy model (loaded, and not on the LOW tier). */
export function weaponUsesGlb(id: WeaponId): boolean {
  return glbModels.has(id) && qualityTier() !== 'low';
}

/** (Re)build a weapon group's levels and its muzzle anchor. */
function fillWeapon(g: THREE.Group, id: WeaponId): void {
  for (const c of [...g.children]) g.remove(c);
  const glb = weaponUsesGlb(id) ? glbModels.get(id)! : null;
  if (id !== 'fists') {
    const lod = new THREE.LOD();
    if (glb) {
      const near = new THREE.Group();
      near.name = 'glb';
      const mesh = new THREE.Mesh(glb.geo, glb.mat);
      mesh.castShadow = true;
      near.add(mesh);
      lod.addLevel(near, 0);
      lod.addLevel(level(id, 'low'), WEAPON_GLB_DISTANCE, 0.08);
    } else {
      lod.addLevel(level(id, 'high'), 0);
      lod.addLevel(level(id, 'low'), WEAPON_LOD_DISTANCE, 0.08); // a little hysteresis: no popping as the camera breathes
    }
    g.add(lod);
  }
  const fixed = MUZZLE[id];
  const mz = glb?.muzzle ?? (fixed ? new THREE.Vector3(0, fixed[0], fixed[1]) : null);
  if (mz) {
    const o = new THREE.Object3D();
    o.name = 'muzzle';
    o.position.copy(mz);
    g.add(o);
  }
}

export function buildWeaponMesh(id: WeaponId): THREE.Group {
  const g = new THREE.Group();
  g.name = `weapon-${id}`;
  fillWeapon(g, id);
  if (glbLoading && !glbModels.has(id) && GLB_FIT[id]) pendingUpgrade.set(g, id);
  g.scale.setScalar(WEAPON_VISUAL_SCALE[id]);
  return g;
}

/** A small cardboard box of rounds (dropped hauls): buff card with a printed label band. */
export function buildAmmoBox(): THREE.Group {
  const m = materials();
  if (!ammoGeo) {
    const k = new Kit();
    k.add('matte', new THREE.BoxGeometry(0.12, 0.06, 0.08), C.card);
    k.add('matte', new THREE.BoxGeometry(0.1215, 0.026, 0.0815), C.label);
    k.add('matte', new THREE.BoxGeometry(0.1222, 0.006, 0.0822).translate(0, 0.006, 0), 0x8e2a1e);
    ammoGeo = k.build();
  }
  const g = new THREE.Group();
  for (const [finish, geo] of Object.entries(ammoGeo) as [Finish, THREE.BufferGeometry][]) {
    const mesh = new THREE.Mesh(geo, m[finish]!);
    mesh.castShadow = true;
    g.add(mesh);
  }
  return g;
}

/** Shared casing geometry: a short brass case (revolver) or a red paper cartridge (shotgun). */
export function casingGeometry(): THREE.BufferGeometry {
  return new THREE.CylinderGeometry(0.008, 0.008, 0.035, 6, 1);
}

/**
 * Muzzle flash, pointing down local +Z (vertex colours, for additive blending: black is invisible, so
 * the edges fade without a texture): a star burst across the bore and a flame tongue in both the
 * horizontal and vertical planes, so it reads from the high camera and from the side alike.
 */
export function muzzleFlashGeometry(): THREE.BufferGeometry {
  const pos: number[] = [];
  const col: number[] = [];
  const core = [1, 0.96, 0.82];
  const mid = [1, 0.62, 0.22];
  const rim = [0.35, 0.12, 0.02];
  const tri = (a: number[], ca: number[], b: number[], cb: number[], c: number[], cc: number[]): void => {
    pos.push(...a, ...b, ...c);
    col.push(...ca, ...cb, ...cc);
  };
  // Star burst (XY plane): eight spikes of uneven length.
  const spikes = 8;
  for (let i = 0; i < spikes * 2; i++) {
    const a0 = (i / (spikes * 2)) * Math.PI * 2;
    const a1 = ((i + 1) / (spikes * 2)) * Math.PI * 2;
    const r0 = i % 2 ? 0.06 : i % 4 ? 0.15 : 0.21;
    const r1 = (i + 1) % 2 ? 0.06 : (i + 1) % 4 ? 0.15 : 0.21;
    tri([0, 0, 0.02], core, [Math.cos(a0) * r0, Math.sin(a0) * r0, 0.02], i % 2 ? mid : rim, [Math.cos(a1) * r1, Math.sin(a1) * r1, 0.02], (i + 1) % 2 ? mid : rim);
  }
  // Flame tongues: a fan from the muzzle, wide at the root and long down the line of fire.
  const tongue: [number, number, number[]][] = [
    [0, -0.03, mid],
    [0.07, 0.06, mid],
    [0.08, 0.16, rim],
    [0.035, 0.3, mid],
    [0, 0.46, rim],
    [-0.035, 0.3, mid],
    [-0.08, 0.16, rim],
    [-0.07, 0.06, mid],
  ];
  for (const plane of [0, 1]) {
    const p = (w: number, l: number): number[] => (plane ? [0, w, l] : [w, 0, l]);
    for (let i = 0; i < tongue.length; i++) {
      const [w0, l0, c0] = tongue[i]!;
      const [w1, l1, c1] = tongue[(i + 1) % tongue.length]!;
      tri(p(0, 0.1), core, p(w0, l0), c0, p(w1, l1), c1);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  return g;
}

/**
 * Tracer streak along local +Z from 0 to 1 (scaled to the shot's length): two crossed ribbons, thin and
 * dim at the muzzle end, bright at the round's end, so it reads as something travelling out.
 */
export function tracerGeometry(): THREE.BufferGeometry {
  const pos: number[] = [];
  const col: number[] = [];
  const tail = [0.18, 0.1, 0.03];
  const head = [1, 0.88, 0.6];
  for (const plane of [0, 1]) {
    const p = (w: number, l: number): number[] => (plane ? [0, w, l] : [w, 0, l]);
    const a = p(-0.008, 0);
    const b = p(0.008, 0);
    const c = p(0.022, 1);
    const d = p(-0.022, 1);
    pos.push(...a, ...b, ...c, ...a, ...c, ...d);
    col.push(...tail, ...tail, ...head, ...tail, ...head, ...head);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  return g;
}

/** Remove a weapon (or pick-up) from the scene; geometry shared through the cache stays for the next one. */
export function disposeWeaponMesh(g: THREE.Object3D): void {
  pendingUpgrade.delete(g as THREE.Group);
  g.traverse((o) => {
    if (o instanceof THREE.Mesh && !o.geometry.userData.weaponShared) o.geometry.dispose();
  });
  g.removeFromParent();
}
